import { AntDesign } from "@expo/vector-icons";
import { BlurView } from "expo-blur";
import { router } from "expo-router";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Modal, View, Text, Pressable } from "react-native";
import { useDispatch } from "react-redux";

import Loader from "@/components/Loader";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import i18n from "@/locales/i18n";
import { sendRequest } from "@/utils/ApiUtil";
import { checkIcon, checkAsset } from "@/utils/transactions/ExchangeCurrencies";

interface ReviewModalProps {
  visible: boolean;
  sourceAsset?: string;
  destinationAsset?: string;
  amountToDebit?: number;
  amountToCredit?: number;
  date?: string;
  id?: string;
  onClick?: () => void;
  onAccept?: () => void;
  onClose: () => void;
  fee: number;
  rate: number;
  timeRemaining: number;
}

export default function ExchangeModal(props: ReviewModalProps) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const globalValues = useAppSelector((state: any) => state);

  const { handleSubmit, formState } = useForm<FormData>({
    mode: "onSubmit",
  });
  const { isSubmitting } = formState;

  useEffect(() => {
    if (props.timeRemaining === 0 && !isSubmitting) {
      props.onClose();
    }
  }, [props.timeRemaining]);

  const onSubmit = async () => {
    props.onAccept();
    try {
      const request = await sendRequest(
        "post",
        "/exchange/accept",
        { id: props.id },
        true,
        true
      );
      if (request) {
        props.onClose();
        props.onClick();
        dispatch(setForm({ transactionResponse: "SUCCESS" }));
        dispatch(setForm({ transactionModalVisible: true }));
        dispatch(setForm({ transactionsHasChanges: true }));
        router.push("/home");
      }
    } catch (error: any) {
      if (
        error.response.data.name === "TimeoutError" ||
        error.response.status === 504 ||
        error.status === 504
      ) {
        props.onClose();
        dispatch(setForm({ transactionResponse: "TIMEOUT" }));
        dispatch(setForm({ transactionModalVisible: true }));
        dispatch(setForm({ transactionsHasChanges: true }));
        router.push("/home");
      } else {
        props.onClose();
        dispatch(setForm({ transactionResponse: "FAILED" }));
        dispatch(setForm({ transactionModalVisible: true }));
        router.push("/home");
      }
    }
  };

  const minutes = Math.floor(props.timeRemaining / 60);
  const seconds = props.timeRemaining % 60;

  return (
    <Modal visible={props.visible} transparent animationType="fade">
      <BlurView intensity={20} className="flex-1 justify-center items-center">
        <View className="bg-white1 border border-gray rounded-lg w-full h-full p-6 items-center justify-center relative lg:w-112 lg:h-136 shadow">
          <Title text={t("common:modal.reviewexchange.title")} />
          <Pressable className="absolute right-3 top-3" onPress={props.onClose}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
          <View className="flex-grow w-full items-center justify-center">
            <View className="flex-col w-full items-center justify-between p-4 border-b border-darkergray">
              <View className="w-full items-center my-2">
                <Text className="text-xl font-thin mb-2">{`${t(
                  "home/exchange:remainingtime"
                )}: ${minutes.toString().padStart(2, "0")}:${seconds
                  .toString()
                  .padStart(2, "0")}`}</Text>
              </View>
              <View className="flex-row w-full items-center justify-center my-2">
                <View className="items-center mx-5">
                  {checkIcon(props.sourceAsset)}
                  <Text className="text-lg">
                    {checkAsset(props.sourceAsset)}
                  </Text>
                </View>
                <AntDesign name="arrowright" size={24} color="black" />
                <View className="items-center mx-5">
                  {checkIcon(props.destinationAsset)}
                  <Text className="text-lg">
                    {checkAsset(props.destinationAsset)}
                  </Text>
                </View>
              </View>
              <View className="flex-row w-full items-start justify-between">
                <Text className="font-thin text-lg">
                  {t("home/exchange:date")}:
                </Text>
                <Text className="text-lg">
                  {new Date(props.date).toLocaleString(i18n.language, {
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    hour12: true,
                  })}
                </Text>
              </View>
              <View className="flex-row w-full items-start justify-between">
                <Text className="font-thin text-lg">
                  {`${t("home/exchange:amounttodebit")} (${checkAsset(
                    props.sourceAsset
                  )}):`}
                </Text>
                <View className="flex-row items-center">
                  <Text className="text-lg ml-2">
                    {checkAsset(props.sourceAsset) === "USD"
                      ? new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: "USD",
                        }).format(props.amountToDebit)
                      : props.amountToDebit}
                  </Text>
                </View>
              </View>
              <View className="flex-row w-full items-start justify-between">
                <Text className="font-thin text-lg">
                  {`${t("home/exchange:amounttocredit")} (${checkAsset(
                    props.destinationAsset
                  )}):`}
                </Text>
                <View className="flex-row items-center">
                  <Text className="text-lg ml-2">
                    {checkAsset(props.destinationAsset) === "USD"
                      ? new Intl.NumberFormat(i18n.language, {
                          style: "currency",
                          currency: "USD",
                        }).format(props.amountToCredit)
                      : props.amountToCredit}
                  </Text>
                </View>
              </View>
            </View>
            <View className="w-full flex-col items-start p-4">
              <View className="flex-row justify-between w-full my-1">
                <Text className="font-thin text-lg">
                  {t("home/exchange:fees.crypto")}:
                </Text>
                <Text className="text-lg">{`${props.fee * 100}%`}</Text>
              </View>
              <View className="flex-row justify-between w-full my-1">
                <Text className="font-thin text-lg">
                  {t("home/exchange:fees.rate")}:
                </Text>
                <Text className="text-lg">
                  {new Intl.NumberFormat(i18n.language, {
                    style: "currency",
                    currency: "USD",
                    minimumFractionDigits: 4,
                  }).format(props.rate)}
                </Text>
              </View>
            </View>
          </View>
          <Button
            label={t("common:button.confirm")}
            onClick={handleSubmit(onSubmit)}
            blocked={isSubmitting}
          />
          {isSubmitting && <Loader />}
        </View>
      </BlurView>
    </Modal>
  );
}
